import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FinContratFilterParams } from '@models/api/requests';
import { ReferentielsService } from 'src/app/core/services/referentiels.service';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { VirtualAutocompleteComponent } from 'src/app/shared/components/virtual-autocomplete/virtual-autocomplete.component';
import { VirtualAutocompleteConfig } from 'src/app/shared/components/virtual-autocomplete/virtual-autocomplete.interfaces';
import { SharedFilterDataService } from 'src/app/core/services/shared-filter-data.service';

@Component({
    selector: 'app-fin-contrat-filter',
    imports: [
        CommonModule,
        MatIconModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatInputModule,
        VirtualAutocompleteComponent
    ],
    templateUrl: './fin-contrat-filter.component.html',
    styleUrls: ['./fin-contrat-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FinContratFilterComponent implements OnInit {
  filterForm!: FormGroup;
  @Output() filterApplied = new EventEmitter<FinContratFilterParams>();

  // Data observables for autocomplete components
  agentsList$!: Observable<any[]>;
  servicesList$!: Observable<any[]>;
  postesList$!: Observable<any[]>;
  
  categoriePosteList: any[] = [];
  typeContratList: any[] = [];

  // Configuration for autocomplete components
  agentConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (agent: any) => {
      if (!agent) return '';
      const nom = agent.nom || '';
      const prenom = agent.prenom || '';
      return `${nom} ${prenom}`.trim();
    },
    filterFn: (agents: any[], searchText: string) => {
      if (!searchText) return agents;
      const filterValue = searchText.toLowerCase().split(' ').join('');
      return agents.filter((agent: any) => {
        if (!agent) return false;
        const nom = agent.nom || '';
        const prenom = agent.prenom || '';
        return (nom + prenom).toLowerCase().split(' ').join('').includes(filterValue);
      });
    }
  };

  serviceConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (service: any) => service ? service.libelle : '',
    filterFn: (services: any[], searchText: string) => {
      if (!searchText) return services;
      const filterValue = searchText.toLowerCase();
      return services.filter((service: any) => 
        service.libelle.toLowerCase().includes(filterValue)
      );
    },
    itemSize: 48
  };

  posteConfig: Partial<VirtualAutocompleteConfig<any>> = {
    displayFn: (poste: any) => {
      if (!poste) return '';
      return poste.code && poste.libelle ? `${poste.code} - ${poste.libelle}` : '';
    },
    filterFn: (postes: any[], searchText: string) => {
      if (!searchText) return postes;
      const filterValue = searchText.toLowerCase();
      return postes.filter((poste: any) => {
        const posteStr = poste.code && poste.libelle ? `${poste.code} - ${poste.libelle}` : '';
        return posteStr.toLowerCase().includes(filterValue);
      });
    },
    itemSize: 48
  };

  constructor(
    private readonly referentielsService: ReferentielsService,
    private readonly formBuilder: FormBuilder,
    private readonly sharedFilterDataService: SharedFilterDataService
  ) {}

  // #region Getters for Form Controls (Optimization for Template Bindings)
  get agentControl() {
    return this.filterForm.get('agent') as FormControl;
  }

  get infoServiceControl() {
    return this.filterForm.get('infoService') as FormControl;
  }

  get infoPosteControl() {
    return this.filterForm.get('infoPoste') as FormControl;
  }

  get agentRemplaceControl() {
    return this.filterForm.get('agentRemplace') as FormControl;
  }
  // #endregion

  ngOnInit() {
    this.filterForm = this.formBuilder.group({
      agent: [''],
      fonction: [''],
      structureHierarchique: [''],
      dateRetourDirection: [''],
      dateDebutContrat: [''],
      dateFinContrat: [''],
      infoService: [''],
      dateEntreeOrganisation: [''],
      posteBudgete: [''],
      typeCT: [''],
      codeEngagement: [''],
      infoPoste: [''],
      agentRemplace: [''],
      dateExpirationCarteSejour: [''],
      categoriePoste: [''],
      grade: [''],
      tauxSitAdmin: [''],
      statut: ['']
    });

    // Initialize data observables for autocomplete components
    this.agentsList$ = this.sharedFilterDataService.loadAgents();
    this.servicesList$ = this.sharedFilterDataService.loadServices();
    this.postesList$ = this.sharedFilterDataService.loadPostes();
    
    this.loadCategoriePoste();
    this.loadTypeContrat();
  }

  loadCategoriePoste() {
    this.referentielsService
      .getCategoriePoste()
      .subscribe((data: any[]) => {
        if (data) {
          this.categoriePosteList = data.filter((o) => o.libelle && o.libelle.trim() !== '');
        }
      });
  }

  loadTypeContrat() {
    this.referentielsService
      .getTypeContrat()
      .subscribe((data: any[]) => {
        if (data) {
          this.typeContratList = data.filter((o) => o.libelle && o.libelle.trim() !== '');
        }
      });
  }

  /**
   * Clear non-autocomplete filter controls - still needed for regular inputs and selects
   */
  clearStructureHierarchiqueFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('structureHierarchique')?.setValue('');
  }

  clearFonctionFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('fonction')?.setValue('');
  }

  clearPosteBudgeteFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('posteBudgete')?.setValue('');
  }

  clearTypeCTFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('typeCT')?.setValue('');
  }

  clearCodeEngagementFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('codeEngagement')?.setValue('');
  }

  clearCategoriePosteFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('categoriePoste')?.setValue('');
  }

  clearGradeFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('grade')?.setValue('');
  }

  clearTauxSitAdminFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('tauxSitAdmin')?.setValue('');
  }

  clearStatutFilter(event: Event) {
    event.stopPropagation();
    this.filterForm.get('statut')?.setValue('');
  }

  categoriePosteDisplayFn(categorie: any): string {
    return categorie ? categorie.code : '';
  }

  typeContratDisplayFn(type: any): string {
    return type ? type.libelle : '';
  }

  resetForm() {
    this.filterForm.reset();
    this.filterApplied.emit(this.filterForm.value);
  }

  applyFilters() {
    this.filterApplied.emit(this.filterForm.value);
  }
}
